import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { ErrorState } from '@heyroot/ui/primitives/ErrorState';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from '@heyroot/ui/primitives/Table';
import { Drawer } from '@heyroot/ui/primitives/Drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { TypedConfirm } from '../typed-confirm';
import {
  fetchTenantUsers,
  fetchPendingInvites,
  fetchTenantRoles,
  inviteTenantUser,
  reissueOwnerInvite,
  setTenantUserStatus,
  queryKeys,
  AdminApiError,
} from '../../lib/api';

// Exemplar: docs/design-system/exemplars -- membership directory list
// (closest existing surface: apps/admin's own staff-list.tsx / users-list.tsx
// pattern -- a dense table of people rows with a status action per row).
//
// Fan-out rows: a user with two roles in one tenant is TWO rows sharing
// userId, distinguished by roleId. React key MUST be the composite
// (tenantId, userId, roleId) -- a plain userId key silently drops the
// second row (slice 3's trap 9); tests assert row COUNT via getAllByRole('row'),
// never text presence, for exactly this reason.

function formatExpiry(iso: string): { label: string; expired: boolean } {
  const d = new Date(iso);
  const expired = d.getTime() < Date.now();
  return { label: d.toLocaleDateString(), expired };
}

export function UsersTab({
  tenantId,
  canWrite,
  canReissueOwnerInvite = false,
}: {
  tenantId: string;
  canWrite: boolean;
  /** TENANTS_CREATE: the re-send mints an owner credential, same bar as creating the tenant. */
  canReissueOwnerInvite?: boolean;
}) {
  const qc = useQueryClient();
  const [reissueNotice, setReissueNotice] = useState<{
    tone: 'ok' | 'warn' | 'error';
    text: string;
  } | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<{ userId: string; label: string } | null>(
    null,
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: queryKeys.tenantUsers(tenantId),
    queryFn: () => fetchTenantUsers(tenantId),
  });
  // Ownerless tenant is a real, reachable state (spec §2.15/§5): an
  // admin-created tenant has no owner until its invite is accepted. Fetched
  // independently of the users list so a pending invite still renders even
  // when the membership table is completely empty.
  const invitesQuery = useQuery({
    queryKey: queryKeys.pendingInvites(tenantId),
    queryFn: () => fetchPendingInvites(tenantId),
  });

  const statusMutation = useMutation({
    mutationFn: (vars: { userId: string; status: 'active' | 'suspended'; reason: string }) =>
      setTenantUserStatus(tenantId, vars.userId, vars.status, vars.reason),
    onSuccess: () => {
      setSuspendTarget(null);
      void qc.invalidateQueries({ queryKey: queryKeys.tenantUsers(tenantId) });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (body: { email: string; roleId: string }) => inviteTenantUser(tenantId, body),
    onSuccess: () => {
      setInviteOpen(false);
      setInviteError(null);
      void qc.invalidateQueries({ queryKey: queryKeys.tenantUsers(tenantId) });
      void qc.invalidateQueries({ queryKey: queryKeys.pendingInvites(tenantId) });
    },
    onError: (err) => {
      setInviteError(err instanceof AdminApiError ? err.message : 'Could not send the invite.');
    },
  });

  // Ownerless-tenant recovery (#14813). Success means the module minted a fresh invite and
  // cancelled the older ones; whether the EMAIL went out is reported separately, because the
  // send is best-effort and the operator's next move differs.
  const reissueMutation = useMutation({
    mutationFn: () => reissueOwnerInvite(tenantId),
    onSuccess: (res) => {
      const to = res.data.targetEmail;
      setReissueNotice(
        res.inviteEmail.ok
          ? {
              tone: 'ok',
              text: `Owner invite re-sent to ${to}. Any earlier owner invite link has been cancelled.`,
            }
          : {
              tone: 'warn',
              text: `A new owner invite was created for ${to}, but the email could not be sent. Click Re-send owner invite to try again.`,
            },
      );
      void qc.invalidateQueries({ queryKey: queryKeys.pendingInvites(tenantId) });
    },
    onError: (err) => {
      setReissueNotice({
        tone: 'error',
        text: err instanceof AdminApiError ? err.message : 'Could not re-send the owner invite.',
      });
      // A 409 "already has members" means the list on screen is stale.
      void qc.invalidateQueries({ queryKey: queryKeys.tenantUsers(tenantId) });
    },
  });

  if (usersQuery.isPending || invitesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-[85%]" />
      </div>
    );
  }
  if (usersQuery.isError) {
    return (
      <ErrorState title="Could not load tenant users" onRetry={() => void usersQuery.refetch()} />
    );
  }

  const rows = usersQuery.data?.rows ?? [];
  // Fix round 2 (I8): swallowing the invites fetch's error into an empty
  // array made an ownerless tenant with a FAILING invites fetch render "No
  // members yet" -- the misleading empty table spec §2.15 explicitly
  // forbids, with no hint anything failed. Surface it instead.
  const pendingInvites = invitesQuery.isError ? [] : (invitesQuery.data?.rows ?? []);
  const invitesFailed = invitesQuery.isError && rows.length === 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-body-sm text-secondary">
          {rows.length} {rows.length === 1 ? 'row' : 'rows'}
        </span>
        <div className="flex items-center gap-2">
          {canReissueOwnerInvite && rows.length === 0 && (
            <Button
              variant="secondary"
              disabled={reissueMutation.isPending}
              onClick={() => {
                setReissueNotice(null);
                reissueMutation.mutate();
              }}
            >
              {reissueMutation.isPending ? 'Sending…' : 'Re-send owner invite'}
            </Button>
          )}
          {canWrite && (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              Invite member
            </Button>
          )}
        </div>
      </div>

      {reissueNotice && (
        <div
          role={reissueNotice.tone === 'ok' ? 'status' : 'alert'}
          className={`rounded-control border p-3 text-body-sm ${
            reissueNotice.tone === 'ok'
              ? 'border-success bg-success-pale text-success-text'
              : reissueNotice.tone === 'warn'
                ? 'border-warning bg-warning-pale text-warning-text'
                : 'border-danger bg-danger-pale text-danger-text'
          }`}
        >
          {reissueNotice.text}
        </div>
      )}

      {/* Fix round 2 (I8): invites-repo.ts returns EVERY pending invite for
          this tenant, not only the bootstrap owner invite -- the "Invite
          member" button above creates exactly those. The copy no longer
          claims a role it cannot know without checking targetRoleId. */}
      {pendingInvites.map((inv) => {
        const { label, expired } = formatExpiry(inv.expiresAt);
        const roleLabel = inv.targetRoleId === 'owner' ? 'Owner invite' : 'Invite';
        return (
          <div
            key={inv.id}
            className={`rounded-control border p-3 text-body-sm ${
              expired
                ? 'border-warning bg-warning-pale text-warning-text'
                : 'border-info bg-info-pale text-info-text'
            }`}
          >
            {expired ? (
              <>
                {roleLabel} to {inv.targetEmail} expired {label}. Not yet accepted.
              </>
            ) : (
              <>
                Invited, not yet accepted — {inv.targetEmail}, expires {label}.
              </>
            )}
          </div>
        );
      })}

      {/* Fix round 2 (I8): a failing invites fetch used to fall through
          silently to "No members yet" -- the misleading empty table spec
          §2.15 explicitly forbids, with no hint anything failed. */}
      {invitesFailed && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
        >
          Could not check whether this tenant has a pending owner invite. This tenant may not
          actually be empty.
        </div>
      )}

      {rows.length === 0 && pendingInvites.length === 0 && !invitesFailed ? (
        <EmptyState
          title="No members yet"
          description="Invite the tenant's first member to get started."
        />
      ) : rows.length === 0 ? null : (
        <div className="overflow-x-auto rounded-control border border-border">
          <Table dense>
            <TableHead>
              <tr>
                <TableHeaderCell>User</TableHeaderCell>
                <TableHeaderCell>Role</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Locations</TableHeaderCell>
                {canWrite && <TableHeaderCell />}
              </tr>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${tenantId}-${row.userId}-${row.roleId ?? 'none'}`}
                  className="hover:bg-surface-elevated transition-colors"
                >
                  <TableCell className="min-w-0 truncate">
                    {row.profile?.username ?? (
                      <span className="font-mono text-caption text-muted">{row.userId}</span>
                    )}
                  </TableCell>
                  <TableCell>{row.roleName ?? <span className="text-muted">—</span>}</TableCell>
                  <TableCell>
                    {row.profile ? (
                      <Badge variant={row.profile.status === 'active' ? 'success' : 'warning'}>
                        {row.profile.status}
                      </Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-caption text-secondary">
                    {row.locations.length > 0 ? row.locations.join(', ') : '—'}
                  </TableCell>
                  {canWrite && (
                    <TableCell>
                      {row.profile?.status === 'active' && (
                        <Button
                          variant="link"
                          className="text-danger-text"
                          onClick={() =>
                            setSuspendTarget({
                              userId: row.userId,
                              label: row.profile?.username ?? row.userId,
                            })
                          }
                        >
                          Suspend
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {suspendTarget && (
        <TypedConfirm
          open={suspendTarget !== null}
          title="Suspend member"
          body={`This immediately revokes ${suspendTarget.label}'s access to this tenant.`}
          confirmValue={suspendTarget.label}
          confirmLabel="Suspend"
          requireReason
          // Fix round 2 (I5): the tenant-USER status route's SetStatusRequest
          // schema is `z.object({ status })` -- reason reaches nothing. Do not
          // claim otherwise on this call site.
          reasonLabel="Reason"
          pending={statusMutation.isPending}
          onCancel={() => setSuspendTarget(null)}
          onConfirm={(reason) =>
            statusMutation.mutate({ userId: suspendTarget.userId, status: 'suspended', reason })
          }
        />
      )}

      <Drawer
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          setInviteError(null);
        }}
        title="Invite member"
      >
        <InviteForm
          tenantId={tenantId}
          error={inviteError}
          pending={inviteMutation.isPending}
          onCancel={() => setInviteOpen(false)}
          onSubmit={(email, roleId) => inviteMutation.mutate({ email, roleId })}
        />
      </Drawer>
    </div>
  );
}

// Fix round 2 (I10): the operator used to free-type a role id into a plain
// Input -- fetchTenantRoles was already in api.ts and used two files over
// (roles-tab.tsx). A Select sourced from the tenant's real roles removes an
// entire class of typo-shaped INVALID_REQUEST/UNKNOWN_TARGET_ROLE errors.
function InviteForm({
  tenantId,
  error,
  pending,
  onCancel,
  onSubmit,
}: {
  tenantId: string;
  error: string | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (email: string, roleId: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const rolesQuery = useQuery({
    queryKey: queryKeys.tenantRoles(tenantId, false),
    queryFn: () => fetchTenantRoles(tenantId, false),
  });
  const roles = rolesQuery.data?.roles ?? [];
  const valid = /.+@.+\..+/.test(email) && roleId.trim().length > 0;
  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="text-body-sm text-danger-text">
          {error}
        </p>
      )}
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Email</span>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-body-sm-emphasis text-secondary">Role</span>
        <Select value={roleId} onValueChange={setRoleId}>
          <SelectTrigger aria-label="Role" disabled={rolesQuery.isPending}>
            <SelectValue placeholder="Select a role" />
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!valid || pending}
          loading={pending}
          onClick={() => onSubmit(email.trim(), roleId.trim())}
        >
          Send invite
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
